import type {
  AssistantMessage,
  ChatMessage,
  ChatStatus,
  PendingFunctionCall,
  ToolCall,
  ToolCallState,
} from './types'

/**
 * State managed by the chat reducer. The `messages` array is the
 * canonical render input; `status` drives the input box state.
 *
 * The reducer is a pure function — the hook owns the actual `useState`
 * and feeds events in. This keeps the event-handling logic unit-testable
 * without needing a live Foundry endpoint.
 */
export interface ChatState {
  messages: ChatMessage[]
  status: ChatStatus
  threadId: string | null
}

export const INITIAL_STATE: ChatState = {
  messages: [],
  status: { kind: 'idle' },
  threadId: null,
}

/**
 * Subset of Foundry SSE events the reducer cares about. Foundry emits more
 * (thread.run.step.created without a tool_call type, thread.run.in_progress,
 * etc.) — we treat anything not listed here as a no-op rather than failing
 * loudly, per Microsoft's "handle unknown events gracefully" guidance.
 */
export type StreamEvent =
  | { kind: 'thread.created'; threadId: string }
  | { kind: 'thread.run.created'; runId: string }
  | { kind: 'thread.message.created'; messageId: string }
  | { kind: 'thread.message.delta'; messageId: string; textDelta: string }
  | { kind: 'thread.message.completed'; messageId: string }
  | { kind: 'thread.run.step.tool_call_started'; stepId: string; toolName: string }
  | { kind: 'thread.run.step.tool_call_args_delta'; stepId: string; argsDelta: string }
  | { kind: 'thread.run.step.tool_call_completed'; stepId: string; resultJson: string }
  | {
      kind: 'thread.run.tool_approval_requested'
      approvalRequestId: string
      toolName: string
      argumentsJson: string
      serverLabel: string
    }
  | { kind: 'thread.run.completed' }
  | { kind: 'thread.run.failed'; message: string }
  | { kind: 'thread.run.requires_action'; consentLink: string }
  /**
   * Function-tool flow: `response.completed` arrived with
   * `status: requires_action.type === "submit_tool_outputs"`. The
   * parser walks the response payload for `function_call` items and
   * delivers them here so the hook can dispatch them against the API.
   */
  | {
      kind: 'thread.run.requires_tool_outputs'
      runId: string
      toolCalls: PendingFunctionCall[]
    }
  /**
   * Local-only — emitted by the hook just before it POSTs a single
   * function-call's result. Flips the matching toolCall row from
   * `dispatching` to `completed` (or `failed`) with the result JSON.
   */
  | {
      kind: 'tool_output.completed'
      callId: string
      ok: boolean
      resultJson: string
    }
  | { kind: 'stream.error'; message: string }
  /**
   * Token accounting for one model request, from the Claude parser's
   * `message_start` / `message_delta` frames. Carried as an event rather
   * than logged where it's parsed so the hook owns the single place that
   * decides what to do with it. The reducer deliberately ignores it —
   * usage is diagnostic, not conversation state.
   */
  | {
      kind: 'stream.usage'
      inputTokens: number
      outputTokens: number
      cacheReadInputTokens: number
      cacheCreationInputTokens: number
    }
  // Local-only event injected by the hook before the network call so we
  // render the user's message immediately, not after the first SSE frame.
  | { kind: 'user.message.appended'; messageId: string; text: string }
  // Local-only events emitted by the hook when the user resolves a
  // pending tool approval. `approve: true` flips status back to `sending`
  // so the next response stream attaches to the awaiting bubble; `false`
  // marks the call as failed and returns to idle without a network round
  // trip (Foundry needs the response for accounting, but the UI is free
  // to short-circuit visually).
  | { kind: 'tool_approval.submitted'; approvalRequestId: string }
  | { kind: 'tool_approval.denied'; approvalRequestId: string }
  // Local-only: the hook is about to POST a resume request (after OAuth
  // consent completed in a popup, or any other "re-run with the same
  // previous_response_id" flow). Flips status to `sending` so the input
  // unlocks and the thinking pulse renders; the new response stream
  // then takes over via thread.run.created.
  | { kind: 'thread.resume' }
  // Local-only reset for "New chat".
  | { kind: 'thread.reset' }

export function chatReducer(state: ChatState, event: StreamEvent): ChatState {
  switch (event.kind) {
    case 'thread.reset':
      return INITIAL_STATE

    case 'thread.resume':
      return { ...state, status: { kind: 'sending' } }

    case 'user.message.appended':
      return {
        ...state,
        messages: [...state.messages, { id: event.messageId, role: 'user', text: event.text }],
        status: { kind: 'sending' },
      }

    case 'thread.created':
      return { ...state, threadId: event.threadId }

    case 'thread.run.created': {
      // The Responses API conflates "this turn's id" and "the value to
      // pass as previous_response_id next turn" into the same response.id.
      // Persist it as threadId so the next send() call chains correctly.
      const base = {
        ...state,
        threadId: event.runId,
        status: { kind: 'streaming' as const, runId: event.runId },
      }
      // Approval-continuation: if the most recent assistant bubble is
      // still holding an `awaiting_approval` tool call, this new run is
      // the resumption Foundry kicks off after the SPA POSTed the
      // `mcp_approval_response`. Retag that bubble's runId to the new
      // run id so the incoming mcp_call / message events attach to it,
      // not to a fresh bubble underneath.
      const lastIdx = findLastAssistantIdx(base.messages)
      if (lastIdx !== -1) {
        const last = base.messages[lastIdx]
        if (last?.role === 'assistant' && last.toolCalls.some(isAwaitingApproval)) {
          return {
            ...base,
            messages: base.messages.map((m, i) =>
              i === lastIdx && m.role === 'assistant'
                ? { ...m, runId: event.runId, isStreaming: true }
                : m,
            ),
          }
        }
      }
      return base
    }

    case 'thread.message.created':
      return ensureCurrentRunBubble(state, event.messageId)

    case 'thread.message.delta':
      return updateAssistantMessage(state, event.messageId, msg => ({
        ...msg,
        text: msg.text + event.textDelta,
      }))

    case 'thread.message.completed':
      return updateAssistantMessage(state, event.messageId, msg => ({
        ...msg,
        isStreaming: false,
      }))

    case 'thread.run.step.tool_call_started': {
      // Responses-API event ordering: tool_call_started fires BEFORE
      // message.created within the same run. Ensure the current run's
      // assistant bubble exists (creating a placeholder if not yet
      // present) so the tool call attaches to this turn — not the
      // previous turn's bubble.
      const next = ensureCurrentRunBubble(state, null)
      // Approval-continuation: if the current run's bubble already has
      // an `awaiting_approval` row for this tool, promote it in place
      // (mcpr_… id → call_… id, awaiting_approval → in_progress) rather
      // than appending a second visually-redundant row.
      const runId = next.status.kind === 'streaming' ? next.status.runId : null
      if (runId !== null) {
        const bubbleIdx = next.messages.findIndex(m => m.role === 'assistant' && m.runId === runId)
        const bubble = bubbleIdx === -1 ? null : (next.messages[bubbleIdx] as AssistantMessage)
        const pendingIdx =
          bubble?.toolCalls.findIndex(
            tc => tc.state === 'awaiting_approval' && tc.name === event.toolName,
          ) ?? -1
        if (bubble && pendingIdx !== -1) {
          const promotedCalls = bubble.toolCalls.map((tc, i) =>
            i === pendingIdx
              ? { ...tc, id: event.stepId, state: 'in_progress' as ToolCallState }
              : tc,
          )
          return {
            ...next,
            messages: next.messages.map((m, i) =>
              i === bubbleIdx && m.role === 'assistant' ? { ...m, toolCalls: promotedCalls } : m,
            ),
          }
        }
      }
      return appendToolCallToCurrentRun(next, {
        id: event.stepId,
        name: event.toolName,
        state: 'in_progress',
        argsJson: '',
        resultJson: null,
      })
    }

    case 'thread.run.tool_approval_requested': {
      // Park the call as `awaiting_approval` on the current run's bubble
      // (creating the bubble if it doesn't exist yet — approval items
      // can arrive before `message.created`, same way `tool_call_started`
      // does). Status flips to `tool_approval_required` so the input bar
      // can disable itself and the bubble can render the approval card.
      const next = ensureCurrentRunBubble(state, null)
      const withCall = appendToolCallToCurrentRun(next, {
        id: event.approvalRequestId,
        name: event.toolName,
        state: 'awaiting_approval',
        argsJson: event.argumentsJson,
        resultJson: null,
        serverLabel: event.serverLabel,
      })
      return {
        ...withCall,
        status: {
          kind: 'tool_approval_required',
          approvalRequestId: event.approvalRequestId,
          toolName: event.toolName,
          argumentsJson: event.argumentsJson,
          serverLabel: event.serverLabel,
        },
      }
    }

    case 'tool_approval.submitted':
      // Hand-off to `sending`; the next thread.run.created will retag
      // the existing bubble (see the approval-continuation branch above).
      // The awaiting_approval tool call stays put — `tool_call_started`
      // promotes it once the resumed run emits the real mcp_call.
      return { ...state, status: { kind: 'sending' } }

    case 'tool_approval.denied':
      // Mark the call as failed locally so the row turns red, then idle.
      // We still need to POST mcp_approval_response{approve:false} so
      // Foundry closes the run — the hook handles that side-effect.
      return {
        ...state,
        status: { kind: 'idle' },
        messages: state.messages.map(m =>
          m.role === 'assistant'
            ? {
                ...m,
                toolCalls: m.toolCalls.map(tc =>
                  tc.id === event.approvalRequestId && tc.state === 'awaiting_approval'
                    ? { ...tc, state: 'failed' as ToolCallState, resultJson: '{"denied":true}' }
                    : tc,
                ),
                isStreaming: false,
              }
            : m,
        ),
      }

    case 'thread.run.step.tool_call_args_delta':
      return mutateToolCall(state, event.stepId, tc => ({
        ...tc,
        argsJson: tc.argsJson + event.argsDelta,
      }))

    case 'thread.run.step.tool_call_completed':
      return mutateToolCall(state, event.stepId, tc => ({
        ...tc,
        state: 'completed',
        resultJson: event.resultJson,
      }))

    case 'thread.run.completed': {
      // Foundry emits `response.completed` *after* a `consent_required`
      // or `tool_approval_required` halt — the response object completes
      // its lifecycle even when it ended on a waiting state. We must NOT
      // overwrite those waiting statuses with `idle`, or the banner /
      // approval card flips off in the same React batch and the user
      // never sees the prompt. Preserve the waiting state; only
      // `streaming`/`sending` falls through to `idle`.
      const preservedStatus: ChatStatus =
        state.status.kind === 'consent_required' ||
        state.status.kind === 'tool_approval_required' ||
        state.status.kind === 'tool_outputs_required'
          ? state.status
          : { kind: 'idle' }
      return {
        ...state,
        status: preservedStatus,
        messages: state.messages.map(m =>
          m.role === 'assistant' && m.isStreaming ? { ...m, isStreaming: false } : m,
        ),
      }
    }

    case 'thread.run.failed':
      return { ...state, status: { kind: 'error', message: event.message } }

    case 'thread.run.requires_action':
      return { ...state, status: { kind: 'consent_required', consentLink: event.consentLink } }

    case 'thread.run.requires_tool_outputs': {
      // Two-id bridge: SSE events created the toolCall row under
      // `stepId` (the `fc_...` output-item id). The hook needs to
      // submit `function_call_output` items back to Foundry keyed on
      // `callId` (`call_...`). Rewrite each pending row's id from
      // stepId to callId so subsequent `tool_output.completed` events
      // (which carry callId) match by id without a side lookup table.
      // While we're at it, flip in_progress → dispatching for the UI.
      const stepToCallId = new Map(event.toolCalls.map(c => [c.stepId, c.callId]))
      const messages = state.messages.map(m => {
        if (m.role !== 'assistant') return m
        return {
          ...m,
          toolCalls: m.toolCalls.map(tc => {
            const newCallId = stepToCallId.get(tc.id)
            if (!newCallId) return tc
            return {
              ...tc,
              id: newCallId,
              callId: newCallId,
              state: 'dispatching' as ToolCallState,
            }
          }),
        }
      })
      return {
        ...state,
        messages,
        status: {
          kind: 'tool_outputs_required',
          runId: event.runId,
          toolCalls: event.toolCalls,
        },
      }
    }

    case 'tool_output.completed':
      return {
        ...state,
        messages: state.messages.map(m =>
          m.role === 'assistant'
            ? {
                ...m,
                toolCalls: m.toolCalls.map(tc =>
                  tc.id === event.callId
                    ? {
                        ...tc,
                        state: (event.ok ? 'completed' : 'failed') as ToolCallState,
                        resultJson: event.resultJson,
                      }
                    : tc,
                ),
              }
            : m,
        ),
      }

    case 'stream.error':
      return { ...state, status: { kind: 'error', message: event.message } }

    // Diagnostic only — see the variant's doc comment above. Logged in
    // `useChat`; nothing about the conversation changes.
    case 'stream.usage':
      return state

    default: {
      // Exhaustiveness check — TypeScript will error if a new StreamEvent variant
      // is added without a case above.
      assertNever(event)
      return state
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function assertNever(_value: never): void {
  // Compile-time exhaustiveness guard; runtime never receives a value.
}

function isAwaitingApproval(tc: ToolCall): boolean {
  return tc.state === 'awaiting_approval'
}

function findLastAssistantIdx(messages: ChatMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === 'assistant') return i
  }
  return -1
}

/**
 * Idempotently ensures the current run has an assistant bubble in the
 * message list. Two entry paths into this function:
 *
 * 1. `thread.message.created` arrives with the real messageId. If a
 *    placeholder was already created by an earlier tool_call_started,
 *    update its id to the real one. Otherwise append a new bubble.
 *
 * 2. `thread.run.step.tool_call_started` arrives before the message
 *    item — pass `null` for messageId. We create a placeholder bubble
 *    using `placeholder-<runId>` as the temporary id, which the
 *    `message.created` path later renames to the real messageId.
 *
 * Either way, the resulting state has exactly one assistant bubble
 * tagged with the current `runId`.
 */
function ensureCurrentRunBubble(state: ChatState, messageId: string | null): ChatState {
  if (state.status.kind !== 'streaming') {
    // No active run — shouldn't happen from these event paths, but be
    // defensive. Fall back to appending a fresh bubble with no runId.
    if (messageId === null) return state
    const next: AssistantMessage = {
      id: messageId,
      role: 'assistant',
      runId: '',
      text: '',
      toolCalls: [],
      isStreaming: true,
    }
    return { ...state, messages: [...state.messages, next] }
  }

  const runId = state.status.runId
  const existingIdx = state.messages.findIndex(m => m.role === 'assistant' && m.runId === runId)

  if (existingIdx === -1) {
    // No bubble for this run yet — append one. Use messageId if we have it,
    // otherwise a placeholder keyed on runId that message.created will rename.
    const next: AssistantMessage = {
      id: messageId ?? `placeholder-${runId}`,
      role: 'assistant',
      runId,
      text: '',
      toolCalls: [],
      isStreaming: true,
    }
    return { ...state, messages: [...state.messages, next] }
  }

  if (messageId === null) {
    // Bubble already exists and we have no new id to apply — no-op.
    return state
  }

  // Bubble exists; rename its id to the real messageId (it may have been a
  // placeholder created by an earlier tool_call_started).
  return {
    ...state,
    messages: state.messages.map((m, i) => (i === existingIdx ? { ...m, id: messageId } : m)),
  }
}

function updateAssistantMessage(
  state: ChatState,
  messageId: string,
  fn: (msg: AssistantMessage) => AssistantMessage,
): ChatState {
  return {
    ...state,
    messages: state.messages.map(m => (m.role === 'assistant' && m.id === messageId ? fn(m) : m)),
  }
}

/**
 * Attaches a tool call to the assistant bubble for the current run.
 * Callers must invoke `ensureCurrentRunBubble` first so a target bubble
 * exists.
 */
function appendToolCallToCurrentRun(state: ChatState, toolCall: ToolCall): ChatState {
  if (state.status.kind !== 'streaming') return state
  const runId = state.status.runId
  return {
    ...state,
    messages: state.messages.map(m =>
      m.role === 'assistant' && m.runId === runId
        ? { ...m, toolCalls: [...m.toolCalls, toolCall] }
        : m,
    ),
  }
}

function mutateToolCall(
  state: ChatState,
  stepId: string,
  fn: (tc: ToolCall) => ToolCall,
): ChatState {
  return {
    ...state,
    messages: state.messages.map(m =>
      m.role === 'assistant'
        ? { ...m, toolCalls: m.toolCalls.map(tc => (tc.id === stepId ? fn(tc) : tc)) }
        : m,
    ),
  }
}
